import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { galleryImages, galleryCategories, galleryLocations } from "../data/gallery";
import "./Gallery.css";

const CloseIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="white"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const ChevLeft = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="white"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const ChevRight = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="white"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const PinIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

export default function Gallery() {
  const [searchParams] = useSearchParams();
  const locationParam = searchParams.get("location");

  const [activeCategory, setActiveCategory] = useState("All");
  const [activeLocation, setActiveLocation] = useState(
    locationParam && galleryLocations.includes(locationParam) ? locationParam : "All"
  );
  const [lightboxIdx, setLightboxIdx] = useState(null);

  // Respect a ?location= query param if the person arrives from the
  // Where We Are Located page (or any other link) after the page has mounted.
  useEffect(() => {
    if (locationParam && galleryLocations.includes(locationParam)) {
      setActiveLocation(locationParam);
    }
  }, [locationParam]);

  const filtered = galleryImages.filter((img) => {
    const matchesCategory = activeCategory === "All" || img.category === activeCategory;
    const matchesLocation = activeLocation === "All" || img.location === activeLocation;
    return matchesCategory && matchesLocation;
  });

  const openLightbox = (i) => setLightboxIdx(i);
  const closeLightbox = () => setLightboxIdx(null);
  const prev = () =>
    setLightboxIdx((i) => (i - 1 + filtered.length) % filtered.length);
  const next = () => setLightboxIdx((i) => (i + 1) % filtered.length);

  const handleKeyDown = (e) => {
    if (lightboxIdx === null) return;
    if (e.key === "ArrowLeft") prev();
    if (e.key === "ArrowRight") next();
    if (e.key === "Escape") closeLightbox();
  };

  return (
    <main onKeyDown={handleKeyDown} tabIndex={-1}>
      <section className="page-hero" aria-label="Gallery page hero">
        <div
          className="page-hero-bg"
          style={{
            backgroundImage: "url('/images/gallery/children-02.jpg')",
          }}
        />
        <div className="container page-hero-content">
          <span className="eyebrow" style={{ color: "var(--amber-light)" }}>
            2008 — Present
          </span>
          <h1>Our Gallery</h1>
          <p>
            17+ years of moments, milestones, and the faces behind the mission
            — from Zambia, to South Africa, to Malawi.
          </p>
        </div>
      </section>

      <section
        className="section gallery-section"
        aria-labelledby="gallery-heading"
      >
        <div className="container">
          {/* LOCATION FILTER */}
          <div
            className="gallery-filters gallery-filters-location"
            role="group"
            aria-label="Filter gallery by location"
          >
            {galleryLocations.map((loc) => (
              <button
                key={loc}
                className={`gallery-filter-btn gallery-filter-btn-location${
                  activeLocation === loc ? " active" : ""
                }`}
                onClick={() => setActiveLocation(loc)}
                aria-pressed={activeLocation === loc}
              >
                {loc !== "All" && <PinIcon />}
                {loc}
              </button>
            ))}
          </div>

          {/* CATEGORY FILTER */}
          <div
            className="gallery-filters"
            role="group"
            aria-label="Filter gallery by category"
          >
            {galleryCategories.map((cat) => (
              <button
                key={cat}
                className={`gallery-filter-btn${activeCategory === cat ? " active" : ""}`}
                onClick={() => setActiveCategory(cat)}
                aria-pressed={activeCategory === cat}
              >
                {cat}
              </button>
            ))}
          </div>

          <p className="gallery-count" aria-live="polite">
            {filtered.length} photo{filtered.length !== 1 ? "s" : ""}
            {activeLocation !== "All" ? ` from ${activeLocation}` : ""}
          </p>

          {filtered.length === 0 ? (
            <div className="gallery-empty">
              <p>No photos yet for this combination — check back soon as we add more from the field.</p>
            </div>
          ) : (
            <div className="gallery-grid">
              {filtered.map((img, i) => (
                <button
                  key={img.id}
                  className="gallery-item"
                  onClick={() => openLightbox(i)}
                  aria-label={`View photo: ${img.title}`}
                >
                  <img src={img.thumb} alt={img.title} loading="lazy" />
                  <span className="gallery-item-location">
                    <PinIcon /> {img.location}
                  </span>
                  <div className="gallery-item-overlay">
                    <span className="gallery-item-prog">{img.programme}</span>
                    <span className="gallery-item-title">{img.title}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* LIGHTBOX */}
      {lightboxIdx !== null && (
        <div
          className="lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Photo lightbox"
        >
          <div className="lightbox-backdrop" onClick={closeLightbox} />
          <div className="lightbox-content">
            <button
              className="lightbox-close"
              onClick={closeLightbox}
              aria-label="Close lightbox"
            >
              <CloseIcon />
            </button>
            <button
              className="lightbox-nav lightbox-prev"
              onClick={prev}
              aria-label="Previous photo"
            >
              <ChevLeft />
            </button>
            <div className="lightbox-img-wrap">
              <img
                src={filtered[lightboxIdx].src}
                alt={filtered[lightboxIdx].title}
              />
            </div>
            <button
              className="lightbox-nav lightbox-next"
              onClick={next}
              aria-label="Next photo"
            >
              <ChevRight />
            </button>
            <div className="lightbox-caption">
              <p className="lightbox-location">
                <PinIcon /> {filtered[lightboxIdx].location}
              </p>
              <p className="lightbox-title">{filtered[lightboxIdx].title}</p>
              {filtered[lightboxIdx].caption && (
                <p className="lightbox-desc">{filtered[lightboxIdx].caption}</p>
              )}
              <p className="lightbox-counter">
                {lightboxIdx + 1} / {filtered.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
